/**
=========================================================
* Soft UI Dashboard PRO React - v3.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
// react-router-dom components
import { Link, Redirect, useParams } from "react-router-dom";

// PropTypes
import PropTypes from "prop-types";

// i18n
import i18n from "i18next";
import { Trans, withTranslation } from "react-i18next";
import authenticationEN from "locales/en/authentication.json";

import * as Yup from "yup";
import { Formik, Form } from "formik";

// @mui material components
import Card from "@mui/material/Card";

// Soft UI Dashboard PRO React components
import SuiBox from "components/sui/SuiBox";
import SuiTypography from "components/sui/SuiTypography";
import SuiButton from "components/sui/SuiButton";
import SuiInput from "components/sui/SuiInput";
import SuiSpinner from "components/sui/SuiSpinner";

// Authentication layout components
import BasicLayout from "layouts/components/BasicLayout";
import Error from "components/layout/Error";
import Spinner from "components/layout/Spinner";
// Images
import curved8 from "assets/images/curved-images/curved8.jpg";

import * as authentication from "actions/authentication";
import * as app from "actions/app";

import Constants from "constants/Constants";

const { REFLINK_TYPES } = Constants;

function Reset2({ t }) {
  const { refId } = useParams();
  const dispatch = useDispatch();
  const appStore = useSelector((state) => state.appStore);

  const [initialValues, setInitialValues] = useState({
    username: "",
    password: "",
    passwordConfirmation: "",
  });
  const [loading, setLoading] = useState(true);
  const [submit, setSubmit] = useState(false);
  const [redirect, setRedirect] = useState(false);
  const formik = useRef(null);

  useEffect(() => {
    i18n.addResources("en", "authentication", authenticationEN);
    dispatch(
      app.refLink({ refId, type: REFLINK_TYPES.RESET_PASSWORD }, (error, result) => {
        if (error) {
          setRedirect(`/error/${error.code}`);
        } else {
          setInitialValues({ ...initialValues, username: result.username });
          setLoading(false);
        }
      })
    );
  }, []);

  const onSubmited = (error) => {
    if (error) {
      setRedirect(`/error/${error.code}`);
    } else {
      setRedirect("/");
    }
  };

  const onSubmit = (values) => {
    setSubmit(true);
    dispatch(app.infoClose());
    dispatch(app.errorClose());
    dispatch(authentication.resetPassword2({ ...values, refId }, onSubmited));
    return true;
  };

  const getErrorsFromValidationError = (validationError) => {
    const FIRST_ERROR = 0;
    return validationError.inner.reduce(
      (errors, _error) => ({
        ...errors,
        [_error.path]: _error.errors[FIRST_ERROR],
      }),
      {}
    );
  };

  const validationSchema = () =>
    Yup.object().shape({
      password: Yup.string()
        .required(t("yup:required", { name: t("password") }))
        .min(8, t("yup:min", { name: t("password"), char: 8 })),
      passwordConfirmation: Yup.string().oneOf([Yup.ref("password"), null], t("yup:password")),
    });

  const validate = (getValidationSchema) => (values) => {
    const validationSchemaInst = getValidationSchema(values);
    try {
      validationSchemaInst.validateSync(values, { abortEarly: false });
      return {};
    } catch (_error) {
      return getErrorsFromValidationError(_error);
    }
  };

  if (redirect) return <Redirect to={redirect} />;
  if (loading) return <Spinner />;
  return (
    <BasicLayout
      title={t("authentication:reset.title")}
      description={t("authentication:reset.subtitle")}
      image={curved8}
    >
      <Formik
        initialValues={initialValues}
        validate={validate(validationSchema)}
        onSubmit={onSubmit}
        innerRef={formik}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
          <Card>
            <Form onSubmit={handleSubmit} noValidate name="resetPassword2Form">
              <SuiBox p={3} mb={1} textAlign="center">
                <SuiTypography variant="h5" fontWeight="medium">
                  <Trans>authentication:reset</Trans>
                </SuiTypography>
              </SuiBox>
              <SuiBox pt={2} pb={3} px={3}>
                <Error
                  error={appStore.error}
                  color="primary"
                  onClick={() => {
                    dispatch(app.errorClose());
                  }}
                />
                <SuiBox>
                  <SuiBox mb={2}>
                    <SuiInput
                      name="username"
                      placeholder={t("username")}
                      error={!!touched.username && !!errors.username}
                      helperText={errors.username}
                      onChange={handleChange}
                      value={values.username}
                      disabled
                      onBlur={handleBlur}
                    />
                  </SuiBox>
                  <SuiBox mb={2}>
                    <SuiInput
                      name="password"
                      type="password"
                      placeholder={t("password")}
                      error={!!touched.password && !!errors.password}
                      helperText={errors.password}
                      onChange={handleChange}
                      value={values.password}
                      disabled={submit}
                      onBlur={handleBlur}
                    />
                  </SuiBox>
                  <SuiBox mb={2}>
                    <SuiInput
                      name="passwordConfirmation"
                      type="password"
                      placeholder={t("repeat-password")}
                      error={!!touched.passwordConfirmation && !!errors.passwordConfirmation}
                      helperText={errors.passwordConfirmation}
                      onChange={handleChange}
                      value={values.passwordConfirmation}
                      disabled={submit}
                      onBlur={handleBlur}
                    />
                  </SuiBox>
                  {submit && (
                    <SuiBox mt={1} mb={1}>
                      {" "}
                      <SuiSpinner size={50} minHeight={50} />{" "}
                    </SuiBox>
                  )}
                  <SuiBox mt={4} mb={1}>
                    <SuiButton
                      variant="gradient"
                      color="dark"
                      fullWidth
                      disabled={submit}
                      onClick={() => {
                        formik.current.submitForm();
                      }}
                    >
                      <Trans>authentication:reset</Trans>
                    </SuiButton>
                  </SuiBox>
                  <SuiBox mt={3} textAlign="center">
                    <SuiTypography variant="button" color="text" fontWeight="regular">
                      Already have an account?&nbsp;
                      <SuiTypography
                        component={Link}
                        to="/"
                        variant="button"
                        color="dark"
                        fontWeight="bold"
                        textGradient
                        disabled={submit}
                      >
                        <Trans>authentication:signin</Trans>
                      </SuiTypography>
                    </SuiTypography>
                  </SuiBox>
                </SuiBox>
              </SuiBox>
            </Form>
          </Card>
        )}
      </Formik>
    </BasicLayout>
  );
}

Reset2.propTypes = {
  t: PropTypes.func.isRequired,
};

export default withTranslation()(Reset2);
